import Phaser from "phaser";
import { GAME, ROUND, ATTACKS, P1_KEYS, type AttackKind, type Archetype } from "../config";
import type { AIDifficulty } from "../input/AIControls";
import {
  getStoredAIDifficulty, setStoredAIDifficulty, resetAIDifficulty,
} from "../net/playerIdentity";

const DIFFICULTY_ORDER: AIDifficulty[] = ["novice", "veteran", "master"];
const DIFFICULTY_RU: Record<AIDifficulty, string> = {
  novice: "НОВИЧОК", veteran: "ВЕТЕРАН", master: "МАСТЕР",
};
import { Fighter } from "../fighters/Fighter";
import { preloadFighter, getFighterMeta, tannedSlug, PACK_LABELS, type PackSlug } from "../fighters/loader";
import { HealthBar } from "../ui/HealthBar";
import { detectAndCreate } from "../input/InputManager";
import { KeyboardControls } from "../input/KeyboardControls";
import { GamepadControls } from "../input/GamepadControls";
import { TouchControls, isTouchDevice } from "../input/TouchControls";
import { MultiplayerClient, RemoteControls, snapshotInput, getMpClient, type InputSnapshot } from "../net/MultiplayerClient";
import rosterJson from "../data/roster.json";
import type { RosterEntry } from "../data/rosterTypes";

const ROSTER = rosterJson as RosterEntry[];
const DEFAULT_P1_SLUG = ROSTER[0]?.slug ?? "player1";
const DEFAULT_P2_SLUG = ROSTER[1]?.slug ?? "player2";

export class FightScene extends Phaser.Scene {
  p1!: Fighter;
  p2!: Fighter;
  private p1Slug = DEFAULT_P1_SLUG;
  private p2Slug = DEFAULT_P2_SLUG;
  private online = false;
  private mp?: MultiplayerClient;
  private myRole: "p1" | "p2" = "p1";
  private localControls?: KeyboardControls | GamepadControls | TouchControls;
  private remoteControls?: RemoteControls;
  private remoteInputBuffer: Map<number, InputSnapshot> = new Map();
  private syncFrame = 0;
  private pingDisplay?: Phaser.GameObjects.Text;
  hp1!: HealthBar;
  hp2!: HealthBar;
  timerText!: Phaser.GameObjects.Text;
  centerText!: Phaser.GameObjects.Text;
  timeLeft = ROUND.TIMER_SECONDS;
  roundActive = false;
  p1Wins = 0;
  p2Wins = 0;
  roundDots: Phaser.GameObjects.Arc[] = [];
  private currentRound = 1;
  private freezeUntil = 0;
  private finishCallShown = false;
  private vsLabel?: Phaser.GameObjects.Text;
  private p1Bandana = "#c41e3a";
  private p2Bandana = "#c41e3a";
  private p1Archetype: Archetype = "default";
  private p2Archetype: Archetype = "default";
  private training = false;
  private aiDifficulty: AIDifficulty = "novice";
  private matchResultSent = false;
  private p2NameOverride?: string;

  constructor() { super("Fight"); }

  init(data: {
    p1Slug?: string; p2Slug?: string;
    p1Bandana?: string; p2Bandana?: string;
    p1Archetype?: Archetype; p2Archetype?: Archetype;
    online?: boolean; mp?: MultiplayerClient; myRole?: "p1" | "p2";
    training?: boolean;
    aiDifficulty?: AIDifficulty;
    p2Name?: string;
  }) {
    this.p2NameOverride = data?.p2Name;
    this.online = !!data?.online;
    // ── ПРАВИЛО ТУРНИРНОЙ ФОРМЫ ──────────────────────────────────────────
    // В онлайн-турнире выбор архетипа игнорируется: P1 — красные (raph),
    // P2 — синие (leo), оба бледные. Это main-tournament dress-code.
    if (this.online) {
      this.p1Slug = "boxer_raph";
      this.p2Slug = "boxer_leo";
    } else {
      this.p1Slug = data?.p1Slug ?? DEFAULT_P1_SLUG;
      this.p2Slug = data?.p2Slug ?? DEFAULT_P2_SLUG;
    }
    this.p1Bandana = data?.p1Bandana ?? "#c41e3a";
    this.p2Bandana = data?.p2Bandana ?? "#c41e3a";
    this.p1Archetype = data?.p1Archetype ?? "default";
    this.p2Archetype = data?.p2Archetype ?? "default";
    this.training = !!data?.training;
    this.aiDifficulty = data?.aiDifficulty ?? getStoredAIDifficulty();
    this.mp = data?.mp ?? (this.online ? getMpClient() : undefined);
    this.myRole = data?.myRole ?? "p1";
    this.p1Wins = 0;
    this.p2Wins = 0;
    this.roundDots = [];
    this.remoteInputBuffer = new Map();
    this.syncFrame = 0;
    this.currentRound = 1;
    this.freezeUntil = 0;
    this.finishCallShown = false;
    this.matchResultSent = false;
  }

  preload() {
    preloadFighter(this, this.p1Slug);
    preloadFighter(this, this.p2Slug);
    // В тренировке P2 выходит «загорелым» (отличие сторон). В турнире — оба
    // бледные (форма raph/leo жёстко фиксирована — см. FightScene.init).
    if (!this.online) {
      preloadFighter(this, tannedSlug(this.p2Slug));
    }
  }

  create() {
    const { WIDTH, HEIGHT, GROUND_Y } = GAME;

    // Фон арены — градиент
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x2c1810, 0x4a2818, 0x1a0a05, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    // Татами на полу
    for (let i = 0; i < 20; i++) {
      this.add.rectangle(i * 60, GROUND_Y + 40, 56, 76, 0x6b3010)
        .setStrokeStyle(2, 0x3a1808)
        .setOrigin(0, 0);
    }

    // Banzai лого как декорация
    this.add.text(WIDTH / 2, 60, "B  A  N  Z  A  I", {
      fontFamily: "Arial Black",
      fontSize: "36px",
      color: "#ffffff",
      stroke: "#c41e3a",
      strokeThickness: 4,
    }).setOrigin(0.5).setAlpha(0.3);

    // Земля
    const ground = this.physics.add.staticGroup();
    const groundRect = this.add.rectangle(WIDTH / 2, GROUND_Y + 1, WIDTH, 4, 0x000000)
      .setVisible(false);
    this.physics.add.existing(groundRect, true);
    ground.add(groundRect);

    // Бойцы. Тренировка: P1 бледный, P2 загорелый. Турнир (online): оба бледные
    // в фиксированной форме (правило турнирной dress-code).
    const p1Meta = getFighterMeta(this, this.p1Slug);
    const p2Meta = getFighterMeta(this, this.online ? this.p2Slug : tannedSlug(this.p2Slug));

    let p1Controls: any;
    let p2Controls: any;
    let attachAI: ((p1F: any, p2F: any) => void) | undefined;

    if (this.online && this.mp) {
      // Online: локальные controls только для нашей роли, remote — для оппонента
      const local = this.makeLocalControls();
      this.localControls = local as any;

      const remote = new RemoteControls();
      this.remoteControls = remote;

      if (this.myRole === "p1") {
        p1Controls = local;
        p2Controls = remote;
      } else {
        p1Controls = remote;
        p2Controls = local;
      }

      // Подписка на сообщения сервера
      this.mp.on(e => this.handleNetEvent(e));
    } else {
      // Локальный режим. В тренировке принудительно ставим P2 = AI, иначе
      // на десктопе без сенсора P2 уехал бы на клавиатуру и просто стоял.
      const inputs = detectAndCreate(this, this.aiDifficulty, this.training);
      p1Controls = inputs.p1;
      p2Controls = inputs.p2;
      attachAI = inputs.attachAI;
    }

    this.p1 = new Fighter(this, WIDTH * 0.25, GROUND_Y, "left",
                          p1Controls, p1Meta, this.p1Bandana, this.p1Archetype);
    this.p2 = new Fighter(this, WIDTH * 0.75, GROUND_Y, "right",
                          p2Controls, p2Meta, this.p2Bandana, this.p2Archetype);

    this.p1.setOpponent(this.p2);
    this.p2.setOpponent(this.p1);

    // Hit-effect callback — VFX срабатывают только когда удар прошёл
    const onHit = (att: Fighter, def: Fighter, dmg: number, blocked: boolean, kind: AttackKind) => {
      this.spawnHitEffect(def.body.x, def.body.y - def.meta.frameHeight / 2, kind, blocked);
      const a = ATTACKS[kind];
      if (!blocked && a.shakeDur && a.shakeIntensity) {
        this.cameras.main.shake(a.shakeDur, a.shakeIntensity);
      }
      if (!blocked && a.freezeMs) {
        this.freezeUntil = this.time.now + a.freezeMs;
      }
    };
    this.p1.onAttack = onHit;
    this.p2.onAttack = onHit;

    if (attachAI) attachAI(this.p1, this.p2);

    this.physics.add.collider(this.p1.body, ground);
    this.physics.add.collider(this.p2.body, ground);

    // Health Bars: слева — мой логин (или «ИГРОК»), справа — имя соперника
    // (для тренировки — архетип ЛЕО/МАЙК/…, для турнира — логин оппонента).
    // mp-singleton хранит логин даже когда this.mp не передан в init data (offline-бой).
    const me = getMpClient();
    const p1Label = (me.login || "ИГРОК").toUpperCase();
    const p2Label = (
      this.online
        ? (this.mp?.lastOpponentLogin || me.lastOpponentLogin || "СОПЕРНИК")
        : (this.p2NameOverride || PACK_LABELS[this.p2Slug as PackSlug] || this.p2.name)
    ).toUpperCase();
    this.hp1 = new HealthBar(this, 30, 50, 360, p1Label, false);
    this.hp2 = new HealthBar(this, WIDTH - 30, 50, 360, p2Label, true);

    // VS-плашка между бойцами
    this.vsLabel = this.add.text(WIDTH / 2, 50, "VS", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffd700", stroke: "#c41e3a", strokeThickness: 5,
    }).setOrigin(0.5);

    // Indicator источников ввода
    const sourceLabel = this.online
      ? `ONLINE — ${this.myRole.toUpperCase()} (ты)   ↔   оппонент`
      : `[${(p1Controls as any).source}]   ─   [${(p2Controls as any).source}]`;
    this.add.text(WIDTH / 2, 105, sourceLabel, {
      fontFamily: "monospace", fontSize: "11px",
      color: this.online ? "#88ff88" : "#888888",
    }).setOrigin(0.5);

    // Ping индикатор в углу
    if (this.online) {
      this.pingDisplay = this.add.text(WIDTH - 10, HEIGHT - 10, "", {
        fontFamily: "monospace", fontSize: "12px", color: "#aaaaaa",
      }).setOrigin(1, 1);
    }

    this.timerText = this.add.text(WIDTH / 2, 95, String(this.timeLeft), {
      fontFamily: "Arial Black",
      fontSize: "44px",
      color: "#ffd700",
      stroke: "#000000",
      strokeThickness: 6,
    }).setOrigin(0.5);

    // Над VS-плашкой в тренировке — текущий уровень AI
    if (this.training) {
      const lvlColor =
        this.aiDifficulty === "novice"  ? "#88ff88" :
        this.aiDifficulty === "veteran" ? "#ffaa00" :
                                          "#ff4444";
      this.add.text(WIDTH / 2, 22, `УРОВЕНЬ: ${DIFFICULTY_RU[this.aiDifficulty]}`, {
        fontFamily: "Arial Black",
        fontSize: "18px",
        color: lvlColor,
        stroke: "#000000",
        strokeThickness: 4,
      }).setOrigin(0.5);
    }

    this.refreshRoundIndicators();

    this.centerText = this.add.text(WIDTH / 2, HEIGHT / 2, "", {
      fontFamily: "Arial Black",
      fontSize: "120px",
      color: "#ff3030",
      stroke: "#000000",
      strokeThickness: 10,
    }).setOrigin(0.5).setAlpha(0);

    this.input.keyboard!.on("keydown-ESC", () => this.scene.start("MainMenu"));

    this.startRound();
  }

  private refreshRoundIndicators() {
    // Чистим старые точки
    this.roundDots.forEach(d => d.destroy());
    this.roundDots = [];

    for (let i = 0; i < ROUND.ROUNDS_TO_WIN; i++) {
      const dot1 = this.add.circle(50 + i * 30, 75, 10,
        i < this.p1Wins ? 0xffd700 : 0x444444)
        .setStrokeStyle(2, 0xffffff);
      const dot2 = this.add.circle(GAME.WIDTH - 50 - i * 30, 75, 10,
        i < this.p2Wins ? 0xffd700 : 0x444444)
        .setStrokeStyle(2, 0xffffff);
      this.roundDots.push(dot1, dot2);
    }
  }

  private makeLocalControls() {
    // Online: локальный игрок может быть на любом устройстве
    if (isTouchDevice()) return new TouchControls(this);

    const gp = this.input.gamepad;
    if (gp && gp.gamepads[0]?.connected) return new GamepadControls(this, 0);

    // Default — клавиатура. P1 keys в любом случае (играем за себя).
    return new KeyboardControls(this, P1_KEYS, "KEYBOARD (you)");
  }

  private handleNetEvent(e: any) {
    if (e.type === "input") {
      // Буферизируем вход оппонента по фрейму
      this.remoteInputBuffer.set(e.frame, e.input);
    } else if (e.type === "opponent_left") {
      this.roundActive = false;
      this.showCenter("СОПЕРНИК\nОТКЛЮЧИЛСЯ", 0xff4444, 5000);
      this.time.delayedCall(3000, () => this.scene.start("MainMenu"));
    } else if (e.type === "end") {
      this.roundActive = false;
    }
  }

  private startRound() {
    this.roundActive = false;
    this.finishCallShown = false;
    this.timeLeft = ROUND.TIMER_SECONDS;
    this.timerText.setText(String(this.timeLeft));
    this.p1.resetForNewRound(GAME.WIDTH * 0.25);
    this.p2.resetForNewRound(GAME.WIDTH * 0.75);

    // ROUND баннер в стиле UMK
    this.showCenter(`РАУНД ${this.currentRound}`, 0xffd700, 900);
    this.time.delayedCall(950, () => {
      this.showCenter("FIGHT!", 0xff3030, 700);
      this.cameras.main.flash(220, 255, 255, 255, false);
      this.roundActive = true;

      this.time.addEvent({
        delay: 1000, repeat: -1,
        callback: () => {
          if (!this.roundActive) return;
          this.timeLeft--;
          this.timerText.setText(String(this.timeLeft));
          if (this.timeLeft <= 0) this.endRound("time");
        }
      });
    });
  }

  private showCenter(text: string, color: number, duration: number) {
    this.centerText.setText(text);
    this.centerText.setColor("#" + color.toString(16).padStart(6, "0"));
    this.centerText.setAlpha(1);
    this.centerText.setScale(0.3);
    this.tweens.add({ targets: this.centerText, scale: 1, duration: 200 });
    this.tweens.add({ targets: this.centerText, alpha: 0, duration: 300, delay: duration });
  }

  private endRound(reason: "ko" | "time") {
    this.roundActive = false;

    let winner: "p1" | "p2" | "draw";
    if (this.p1.hp <= 0 && this.p2.hp <= 0) winner = "draw";
    else if (this.p1.hp <= 0) winner = "p2";
    else if (this.p2.hp <= 0) winner = "p1";
    else winner = this.p1.hp > this.p2.hp ? "p1" :
                  this.p1.hp < this.p2.hp ? "p2" : "draw";

    if (winner === "p1")      this.p1Wins++;
    else if (winner === "p2") this.p2Wins++;

    const winnerName = winner === "draw" ? "НИЧЬЯ"
               : winner === "p1"   ? this.p1.name.toUpperCase()
                                   : this.p2.name.toUpperCase();

    // K.O. flash + shake + slow-mo вибрация
    if (reason === "ko") {
      this.cameras.main.shake(450, 0.018);
      this.cameras.main.flash(350, 255, 60, 60, false);
      this.showCenter("K.O.!", 0xff3030, 1500);
    } else {
      this.showCenter("ВРЕМЯ", 0xffff00, 1200);
    }

    this.time.delayedCall(1500, () => {
      if (winner === "draw") this.showCenter("НИЧЬЯ", 0xffffff, 1800);
      else this.showCenter(`${winnerName} WINS`, 0xffd700, 1800);
    });

    this.time.delayedCall(3500, () => {
      if (this.p1Wins >= ROUND.ROUNDS_TO_WIN || this.p2Wins >= ROUND.ROUNDS_TO_WIN) {
        const champWasP1 = this.p1Wins >= ROUND.ROUNDS_TO_WIN;
        const champName = champWasP1 ? this.p1.name : this.p2.name;
        this.showCenter("FLAWLESS\nVICTORY!", 0xffd700, 1500);
        this.time.delayedCall(1700, () => {
          this.showCenter(`${champName.toUpperCase()}\nЧЕМПИОН`, 0xff3030, 2500);
        });
        // отправка match_result серверу (только в online — за тренировку счётчик не увеличиваем)
        if (this.online && this.mp && !this.matchResultSent && this.mp.isAuthed()) {
          this.matchResultSent = true;
          const iWon = (this.myRole === "p1" && champWasP1) || (this.myRole === "p2" && !champWasP1);
          this.mp.reportMatchResult(this.mp.lastOpponentPlayerId, iWon);
        }
        this.time.delayedCall(4500, () => this.afterMatch(champWasP1));
      } else {
        this.currentRound++;
        this.refreshRoundIndicators();
        this.startRound();
      }
    });
  }

  /**
   * Прогрессия сложности после тренировочного матча:
   *   победа → +1 уровень (выше master не идёт; на master показываем
   *           экран «ТЫ ГОТОВ К ТУРНИРУ» и закрываем игру)
   *   поражение → −1 уровень (ниже novice не опускаемся)
   * Вне тренировки (training=false / online) — сразу в MainMenu как раньше.
   */
  private afterMatch(playerWon: boolean) {
    if (!this.training) {
      this.scene.start("MainMenu");
      return;
    }

    const cur = this.aiDifficulty;
    const idx = DIFFICULTY_ORDER.indexOf(cur);

    if (playerWon && cur === "master") {
      // Финал — пройден весь путь. Сбрасываем прогрессию, чтобы следующий
      // заход в тренировку снова начался с novice.
      resetAIDifficulty();
      this.cameras.main.flash(800, 255, 215, 0, false);
      this.showCenter("ТЫ ГОТОВ\nК ТУРНИРУ", 0xffd700, 4000);
      this.time.delayedCall(4500, () => this.scene.start("MainMenu"));
      return;
    }

    let next = cur;
    if (playerWon && idx < DIFFICULTY_ORDER.length - 1) {
      next = DIFFICULTY_ORDER[idx + 1];
    } else if (!playerWon && idx > 0) {
      next = DIFFICULTY_ORDER[idx - 1];
    }

    if (next !== cur) {
      setStoredAIDifficulty(next);
      const arrow = playerWon ? "↑" : "↓";
      const tone = playerWon ? 0x88ff88 : 0xff8888;
      this.showCenter(`${arrow} СЛОЖНОСТЬ: ${DIFFICULTY_RU[next]}`, tone, 2200);
      this.time.delayedCall(2500, () => this.scene.start("Training"));
    } else {
      // На границе (novice + поражение): просто возврат
      this.time.delayedCall(800, () => this.scene.start("Training"));
    }
  }

  // ── VFX impact ──────────────────────────────────────────────────────────
  private spawnHitEffect(x: number, y: number, kind: AttackKind, blocked: boolean) {
    const a = ATTACKS[kind];
    const color = blocked ? 0x88ccff : (a.flash ?? 0xffffff);

    // Иероглифы поверх — UMK-style impact text
    const labelText = blocked ? "БЛОК"
      : kind === "uppercut" ? "АППЕРКОТ!"
      : kind === "sweep"    ? "ПОДСЕЧКА!"
      : kind === "kick"     ? "УДАР!"
      :                       "ХУК!";

    const label = this.add.text(x, y - 30, labelText, {
      fontFamily: "Arial Black", fontSize: "24px",
      color: blocked ? "#88ccff" : "#ffd700",
      stroke: "#000000", strokeThickness: 5,
    }).setOrigin(0.5).setDepth(1500).setScale(0.4);
    this.tweens.add({
      targets: label, scale: 1.3, alpha: 0,
      y: y - 80, duration: 600, ease: "Cubic.easeOut",
      onComplete: () => label.destroy(),
    });

    // «Звезда» — 4 коротких линии расходящихся (импакт кадр)
    const g = this.add.graphics().setDepth(1499);
    g.lineStyle(4, color, 1);
    const len = 26;
    g.lineBetween(x - len, y, x + len, y);
    g.lineBetween(x, y - len, x, y + len);
    g.lineBetween(x - len * 0.7, y - len * 0.7, x + len * 0.7, y + len * 0.7);
    g.lineBetween(x - len * 0.7, y + len * 0.7, x + len * 0.7, y - len * 0.7);
    this.tweens.add({
      targets: g, alpha: 0, scaleX: 1.8, scaleY: 1.8,
      duration: 250, ease: "Cubic.easeOut",
      onComplete: () => g.destroy(),
    });

    // 6 мелких частиц (искры)
    if (!blocked) {
      for (let i = 0; i < 6; i++) {
        const ang = Math.random() * Math.PI * 2;
        const speed = 60 + Math.random() * 80;
        const dx = Math.cos(ang) * speed;
        const dy = Math.sin(ang) * speed;
        const spark = this.add.circle(x, y, 3, color, 1).setDepth(1498);
        this.tweens.add({
          targets: spark,
          x: x + dx, y: y + dy,
          alpha: 0, duration: 380, ease: "Cubic.easeOut",
          onComplete: () => spark.destroy(),
        });
      }
    }
  }

  update(time: number) {
    // hitstop — на эти миллисекунды замораживаем апдейты бойцов
    const frozen = time < this.freezeUntil;

    if (this.online && this.mp && this.localControls && this.remoteControls) {
      if (!frozen) this.updateOnline(time);
    } else if (this.roundActive && !frozen) {
      this.p1.update(time);
      this.p2.update(time);
      if (this.p1.hp <= 0 || this.p2.hp <= 0) {
        this.endRound("ko");
      }
    }

    // «FINISH HIM!» когда у одного < 20% HP, но никто ещё не упал
    if (this.roundActive && !this.finishCallShown) {
      const p1Low = this.p1.hp > 0 && this.p1.hp < 20;
      const p2Low = this.p2.hp > 0 && this.p2.hp < 20;
      if (p1Low || p2Low) {
        this.finishCallShown = true;
        this.showCenter("FINISH HIM!", 0xff2020, 1400);
      }
    }

    this.p1.syncLabel();
    this.p2.syncLabel();
    this.hp1.update(this.p1.hp, this.p1.hpMax);
    this.hp2.update(this.p2.hp, this.p2.hpMax);
  }

  private updateOnline(time: number) {
    if (!this.roundActive) return;

    // Каждый кадр шлём свой input
    this.localControls!.update(time);
    const myInput = snapshotInput(this.localControls!);
    this.mp!.sendInput(this.syncFrame, myInput);

    // Ждём оппонента — если его input для текущего фрейма ещё не пришёл, пропускаем кадр
    const oppInput = this.remoteInputBuffer.get(this.syncFrame);
    if (!oppInput) {
      // Лаги — ставим игру на паузу до прихода
      if (this.pingDisplay) {
        this.pingDisplay.setText(`PING: ${this.mp!.rttMs}ms ⌛waiting frame ${this.syncFrame}`);
      }
      return;
    }

    // Применяем input к remoteControls
    this.remoteControls!.setSnapshot(oppInput);
    this.remoteInputBuffer.delete(this.syncFrame);

    // Запускаем оба бойца на этом кадре
    this.p1.update(time);
    this.p2.update(time);

    if (this.p1.hp <= 0 || this.p2.hp <= 0) {
      this.endRound("ko");
    }

    // Ping HUD
    if (this.pingDisplay) {
      const buf = this.remoteInputBuffer.size;
      this.pingDisplay.setText(`PING: ${this.mp!.rttMs}ms  buf:${buf}  f:${this.syncFrame}`);
    }

    this.syncFrame++;
  }
}
